/**
 * Convert a 32-bit binary string to a signed integer
 * Uses two's complement for negative numbers
 */
export function binaryToInt32(binary: string): number {
  if (binary.length !== 32 || !/^[01]+$/.test(binary)) {
    return 0;
  }

  // Check if negative (first bit is 1)
  const isNegative = binary[0] === '1';

  if (isNegative) {
    // Two's complement: invert bits and add 1
    let inverted = '';
    for (const bit of binary) {
      inverted += bit === '0' ? '1' : '0';
    }
    return -(parseInt(inverted, 2) + 1);
  }

  return parseInt(binary, 2);
}

/**
 * Convert a signed integer to a 32-bit binary string
 * Uses two's complement for negative numbers
 */
export function int32ToBinary(value: number): string {
  // Clamp to 32-bit signed integer range
  const clamped = Math.max(-2147483648, Math.min(2147483647, Math.trunc(value)));

  if (clamped >= 0) {
    return clamped.toString(2).padStart(32, '0');
  }

  // Two's complement for negative numbers
  // Add 2^32 to get unsigned representation
  const unsigned = clamped + 4294967296;
  return unsigned.toString(2).padStart(32, '0');
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * Split a 32-bit binary string into 4 bytes (little-endian order for display)
 * Returns bytes in D-parameter order: [LSB, ..., MSB] for UI display
 */
export function splitIntoBytes(binary: string): [string, string, string, string] {
  // Binary string is big-endian (MSB first), but D-parameters are little-endian
  // So we reverse: binary[0-7] is MSB -> becomes byte[3], binary[24-31] is LSB -> becomes byte[0]
  return [
    binary.slice(24, 32), // LSB (D1554/D1558/etc)
    binary.slice(16, 24),
    binary.slice(8, 16),
    binary.slice(0, 8)    // MSB (D1557/D1561/etc)
  ];
}

/**
 * Join 4 bytes into a 32-bit binary string (from little-endian display order)
 * Bytes are in D-parameter order: [LSB, ..., MSB], converts to big-endian for calculation
 */
export function joinBytes(bytes: [string, string, string, string]): string {
  // Reverse byte order: byte[0] is LSB, byte[3] is MSB
  // For calculation we need MSB first (big-endian)
  return bytes[3] + bytes[2] + bytes[1] + bytes[0];
}

/**
 * Validate a single bit value
 */
export function isValidBit(value: string): boolean {
  return value === '0' || value === '1';
}

/**
 * Validate an 8-bit byte string
 */
export function isValidByte(value: string): boolean {
  return value.length === 8 && /^[01]+$/.test(value);
}
